import { useContext, useEffect, useRef, useState } from "react";

import CoralCommentsContext from "@voxmedia/duet-context/context/coral-comments-context";
import CycloneCommentsContext from "@voxmedia/duet-context/context/cyclone-comments-context";

/**
 * Hook that subscribes to comment count changes for a specific ID.
 */
export const useCommentCount = (
  countId: string,
): {
  count: number | null;
  countId: string;
  updateCommentCount: (count: number, timestamp: number) => void;
} => {
  const coralContext = useContext(CoralCommentsContext);
  const cycloneContext = useContext(CycloneCommentsContext);
  const commentsContext = cycloneContext ?? coralContext;

  if (!countId) {
    throw new Error("useCommentCount requires countId");
  }

  if (!commentsContext) {
    throw new Error("useCommentCount must be used within CommentProvider");
  }

  const {
    getCommentCount,
    updateCommentCount,
    subscribeToCommentCount,
    registerCountId,
  } = commentsContext;

  const [count, setCount] = useState<number | null>(() =>
    getCommentCount(countId),
  );
  const subscriptionRef = useRef<(() => void) | null>(null);
  const hasRegisteredRef = useRef<string | null>(null);

  useEffect(() => {
    if (countId && hasRegisteredRef.current !== countId) {
      registerCountId(countId);
      hasRegisteredRef.current = countId;
    }
  }, [countId, registerCountId]);

  useEffect(() => {
    if (subscriptionRef.current) {
      subscriptionRef.current();
    }

    subscriptionRef.current = subscribeToCommentCount(countId, (newCount) => {
      setCount(newCount);
    });

    const currentCount = getCommentCount(countId);
    if (currentCount !== null) {
      setCount(currentCount);
    }

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current();
        subscriptionRef.current = null;
      }
    };
  }, [countId, getCommentCount, subscribeToCommentCount]);

  const updateCommentCountForId = (count: number, timestamp: number) => {
    updateCommentCount(countId, count, timestamp);
  };

  return {
    count,
    countId,
    updateCommentCount: updateCommentCountForId,
  };
};
